import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  updateAppointment,
  resetAppointment,
} from "./appointments/appointmentSlice";
import ShopAddress from "./ShopAddress";
import { closeButton } from "./css/popupVariables";
import { sendToWindowDataLayer } from "./utils";
import tinycolor from "tinycolor2";
import { AppointmentState } from "./types";

interface HeaderProps {
  appointment: AppointmentState;
}

// Extend AppointmentState to include previousStage property
interface ExtendedAppointmentState extends AppointmentState {
  previousStage?: number;
}

const Header: React.FC<HeaderProps> = ({ appointment }) => {
  const dispatch = useDispatch();

  const headerColor = useMemo((): string => {
    return appointment?.shopData?.headerColor || "#ffffff";
  }, [appointment?.shopData?.headerColor]);

  const fontColor = useMemo((): string => {
    return tinycolor(headerColor).isDark() ? "#ffffff" : "#000000";
  }, [headerColor]);

  const handleBailOut = (): void => {
    if (appointment.stage === 5) {
      const outlet =
        appointment?.shopData?.shopContact?.socialLinks?.[0]?.url || "";
      if (outlet) {
        window.location.replace(outlet);
      }
    } else {
      if (appointment.bailed || appointment.stage === 6) {
        const isInIframe = window.self !== window.top;

        let dispatchData: Partial<AppointmentState> = {};
        if (isInIframe) {
          dispatchData = {
            stage: appointment.entryStage,
            domain: appointment.domain,
            rwg_token: appointment.rwg_token,
            entryStage: appointment.entryStage,
            specId: appointment.specId,
          };

          if (appointment.specId === 0) {
            dispatchData = { ...dispatchData, shopData: appointment.shopData };
          } else if (appointment.specId === 1) {
            dispatchData = {
              stage: 0,
              domain: appointment.domain,
              rwg_token: appointment.rwg_token,
              entryStage: 0,
              specId: appointment.specId,
            };
          }

          dispatch(resetAppointment({ dispatchData: dispatchData }));
          window.parent.postMessage("hideWidget", "*");
        } else {
          if (appointment?.customer?.phone) {
            sendToWindowDataLayer(
              "dc-service-partial_conversion",
              "dc-service-partial_conversion"
            );
          }

          const redirect =
            appointment?.shopData?.shopContact?.socialLinks?.[0]?.url || "";
          if (redirect) {
            window.location.replace(redirect);
          }
        }
      } else {
        // Use the extended appointment state to include previousStage
        const updates: Partial<ExtendedAppointmentState> = {
          stage: 6,
          previousStage: appointment.stage,
        };

        dispatch(
          updateAppointment({
            updates: updates as Partial<AppointmentState>, // Type assertion to match the expected type
          })
        );
      }
    }
  };

  return (
    <div
      className="grid grid-cols-1 pt-4 md:pt-0 md:grid-cols-3 h-auto md:h-[130px] border-b-gray-500"
      style={{
        backgroundColor: headerColor,
        color: fontColor,
      }}
    >
      {/* Close Button Container */}
      <div className="text-start pl-6 flex items-center justify-center md:justify-start relative">
        <div
          className={`${closeButton} absolute top-2 left-2`}
          style={{ color: fontColor, borderColor: fontColor }}
          onClick={handleBailOut}
        >
          X
        </div>
      </div>

      {/* Center Content (Logo or Shop Name) */}
      <div className="text-2xl font-semibold flex flex-col items-center justify-center md:items-center md:justify-center">
        {appointment?.shopData?.shopLogo?.logoUrl ? (
          <>
            <div className="flex items-center justify-center md:h-full md:pt-3">
              <img
                src={appointment?.shopData?.shopLogo?.logoUrl}
                alt="company logo"
                style={{ width: "auto", height: "68px" }}
              />
            </div>
          </>
        ) : (
          <div className="flex items-center h-[68px]">
            {appointment?.shopData?.shopName?.name}
          </div>
        )}
        {appointment?.stage === 0 && (
          <div className="flex items-center justify-center h-full">
            Choose a Location
          </div>
        )}
      </div>

      {/* Shop Address or Other Right-Aligned Content */}
      <div className="flex items-center justify-center md:justify-end md:h-full">
        {(appointment?.stage ?? 0) > 0 && (
          <ShopAddress appointment={appointment} />
        )}
      </div>
    </div>
  );
};

export default Header;
